import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { IMMINENT_THRESHOLD_MINUTES } from "@internal/constants/telemedicine/imminence"
import React, { useState } from "react"
import { useIntl } from "react-intl"
import { Link as ReactRouterLink } from "react-router-dom"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import {
  formatAppointmentDateWithYear,
  formatAppointmentTimeWithZone,
  formatCountdownLabel,
  formatProviderName
} from "./formatting"
import { perViewport, perViewportTypography } from "./responsive"
import { useAppointmentsTracking } from "./tracking"
import { useAppointmentTiming } from "./useAppointmentTiming"
import { usePhotoIdOnFile } from "../intake/photoIdHooks"
import type { UpcomingAppointment } from "./types"

/** How long the copy confirmation stays up, in ms. */
const COPY_CONFIRMATION_MS = 4000

/**
 * What the snackbar says either way. Named rather than inlined at the JSX branch only because the
 * failure sentence is long enough that the ternary reads better without it.
 */
const COPY_SUCCESS_MESSAGE = "Zoom link copied."
const COPY_FAILED_MESSAGE = "We couldn't copy the link. Use Join to open the visit instead."
const JOIN_LATER_MESSAGE = `You'll be able to join from this page ${IMMINENT_THRESHOLD_MINUTES} minutes before your appointment time.`

/** Whether the copy affordance has something to say. */
type CopyState = "idle" | "copied" | "failed"

type UpNextAction = "join" | "completeForms" | "uploadId" | "joinLater"

const resolveAction = ({
  imminent,
  formsComplete,
  needsPhotoId
}: {
  imminent: boolean
  formsComplete: boolean
  needsPhotoId: boolean
}): UpNextAction => {
  if (imminent) return "join"
  if (needsPhotoId) return "uploadId"
  return formsComplete ? "joinLater" : "completeForms"
}

function PrimaryAction({
  action,
  joinUrl,
  canJoin,
  formsUrl,
  onPrepareForVisit,
  onJoin
}: {
  action: UpNextAction
  joinUrl: string
  /** Whether a usable join link is available yet. False renders a disabled Join rather than a dead link. */
  canJoin: boolean
  formsUrl: string
  onPrepareForVisit?: () => void
  onJoin: () => void
}): JSX.Element | null {
  switch (action) {
    case "join":
      // No link yet (Healthie hasn't provisioned it): a disabled Join, not an anchor to nowhere.
      if (!canJoin) {
        return (
          <Button variant="contained" color="secondary" disabled>
            Join
          </Button>
        )
      }
      // A new tab: losing the app's state mid-navigation to join a call is the worse failure.
      return (
        <Button
          variant="contained"
          color="secondary"
          component="a"
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onJoin}
        >
          Join
        </Button>
      )
    case "uploadId":
    case "completeForms":
      // `/telemedicine/booking/:id/forms` resolves the pre-visit intake from the appointment `:id`;
      // that page owns access checks and redirects home if the member can't book.
      return (
        <Button
          variant="contained"
          color="secondary"
          component={ReactRouterLink}
          to={formsUrl}
          onClick={onPrepareForVisit}
        >
          {action === "uploadId" ? "Upload ID" : "Complete forms"}
        </Button>
      )
    case "joinLater":
      // Disabled regardless of `canJoin`: the visit is not in the window yet, even if a cached
      // link resolves one anyway.
      return (
        <Button variant="contained" color="secondary" disabled>
          Join
        </Button>
      )
    default: {
      const unhandled: never = action
      throw new Error(`Unhandled Up next action: ${String(unhandled)}`)
    }
  }
}

/**
 * One booked visit, and it owns no heading: a member can hold several bookings (MEM-848) and
 * "Upcoming" is the section's word for all of them, so the heading belongs to `UpNextAppointments`
 * below — the same split as `PastAppointments` and its row.
 *
 * Exported for its own tests, not for the package barrel — the same arrangement
 * `getMainColumnSections` has. The page composes `UpNextAppointments`; nothing outside this folder
 * should reach for a single card.
 *
 * `onRequestCancel` rather than a dialog of its own: confirming a cancelation removes the
 * appointment, which unmounts this card, and frame 7 draws the canceled dialog over a page that has
 * already dropped it. The card asks; the page owns the flow. See `CancelAppointmentDialog.tsx`.
 *
 * The copy `Snackbar` stays per-card because the state it reports — which visit's link is on the
 * clipboard — is the card's. Only an imminent visit offers the affordance at all, so in practice one
 * card at a time can have anything to say.
 */
export function UpNextCard({
  appointment,
  onRequestCancel,
  bookingPath,
  onPrepareForVisit
}: {
  /**
   * Nullable, and so is its `date`. The section only lists scheduled appointments, but the guard
   * below is what the host's `strictNullChecks: false` used to leave implicit (see
   * `UpNextCard.test.tsx`, which renders both).
   */
  appointment: UpcomingAppointment | null | undefined
  onRequestCancel: (appointment: UpcomingAppointment) => void
  /** The booking route, injected by the host — the package cannot reach its `Paths` enum. */
  bookingPath: string
  onPrepareForVisit?: (appointmentId: string) => void
}): JSX.Element | null {
  const theme = useTheme()
  const intl = useIntl()
  const track = useAppointmentsTracking()
  const [copyState, setCopyState] = useState<CopyState>("idle")
  // Read before the null guard below, because a hook cannot be called conditionally.
  const { now, imminent, joinUrl: resolvedJoinUrl, canJoin } = useAppointmentTiming(appointment)

  const photoId = usePhotoIdOnFile({ enabled: appointment?.status === "scheduled", refreshOnMount: true })
  const needsPhotoId = photoId.data?.onFile === false

  // Guard rather than take the page down with a missing appointment or a missing date on one.
  if (!appointment?.date) {
    return null
  }

  const { id, provider, date, formsComplete, status } = appointment
  const action = resolveAction({ imminent, formsComplete, needsPhotoId })
  const formsUrl = `${bookingPath}/${encodeURIComponent(id)}/forms?source=appointments_page`

  // `status` permits "canceled" and "completed", and nothing stops such a record reaching the
  // `upcoming` slot. Only a scheduled visit can be joined, prepared for or cancelled; whether the
  // card renders at all for the others is page composition, and belongs to CONAPP-270.
  const actionable = status === "scheduled"

  const providerName = formatProviderName(provider)

  const metaLabel = `${formatAppointmentDateWithYear(intl, date)} · ${formatAppointmentTimeWithZone(intl, date)}`

  const copyJoinUrl = async () => {
    try {
      await navigator.clipboard.writeText(resolvedJoinUrl)
      setCopyState("copied")
    } catch {
      // An insecure context and a denied permission both land here. Claiming success when the
      // clipboard is empty is worse than saying it failed, so the state is only set on resolve.
      setCopyState("failed")
    }
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          // `.mob-card` keeps `position: relative` too, so this stays flat rather than becoming
          // another breakpoint object — even though on mobile there is no longer an absolutely
          // positioned child for it to anchor.
          position: "relative",
          // `.mob-card` adds `display: flex; flex-direction: column` so the in-flow chip can
          // `align-self: flex-start` and shrink-wrap. `flexDirection` is inert under
          // `display: block`, so only `display` needs the breakpoint.
          display: perViewport("flex", "block"),
          flexDirection: "column",
          padding: theme.spacing(theme.tokens.spacing.lg),
          borderRadius: theme.tokens.borderRadius.md
        }}
      >
        {/*
          The one element on the page whose LAYOUT changes between the two compositions rather than
          just its measurements. Desktop `.date-chip` is lifted out of flow into the card's
          top-inline-end corner on a `secondary.light` ground; frame 9's `.mob-chip` sits in flow above
          the lead on `background.hover` (`#F0F0F0`, which is the design's `--bg-hover` exactly). It is
          already the Paper's first child, so flow order needs no change — only the positioning does.

          The insets stay flat: they are inert under `position: static`, and spelling them as
          breakpoint objects would add two more media queries to say nothing.
        */}
        <Chip
          label={formatCountdownLabel(intl, date, now)}
          sx={{
            position: perViewport("static", "absolute"),
            alignSelf: "flex-start",
            insetBlockStart: theme.spacing(theme.tokens.spacing.lg),
            insetInlineEnd: theme.spacing(theme.tokens.spacing.lg),
            marginBlockEnd: perViewport(
              theme.spacing(theme.tokens.spacing.md),
              theme.spacing(theme.tokens.spacing.none)
            ),
            backgroundColor: perViewport(theme.palette.background.hover, theme.palette.secondary.light),
            paddingInline: theme.spacing(theme.tokens.spacing.xs)
          }}
        />
        {/*
          `.lead` is `align-items: flex-start`; frame 9's `.mob-lead` centres the portrait against the
          two lines of text instead, which is what keeps a 48px avatar from sitting proud of them.
        */}
        <Stack
          sx={{ flexDirection: "row", alignItems: perViewport("center", "flex-start") }}
          spacing={theme.spacing(theme.tokens.spacing.md)}
        >
          {/* No `size`: the component default IS the appointment-row size, 54px desktop / 48px mobile. */}
          <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
          <Box sx={{ minInlineSize: 0 }}>
            {/* `.nm` → `.mob-nm`, h5 → h6. See `perViewportTypography` for why `variant` stays. */}
            <Typography variant="h5" component="h3" sx={{ typography: perViewportTypography("h6", "h5") }}>
              {providerName}
            </Typography>
            {/* `body1` is already `text.secondary` in the Carotene theme, matching the design's `.mt`. */}
            <Typography sx={{ typography: perViewportTypography("body2", "body1") }}>{metaLabel}</Typography>
            {imminent && actionable && canJoin && (
              <Link
                component="button"
                type="button"
                onClick={copyJoinUrl}
                sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) }}
              >
                Copy Zoom link
              </Link>
            )}
            {actionable && action === "joinLater" && (
              <Typography
                sx={{
                  typography: perViewportTypography("body2", "body1"),
                  color: "text.primary",
                  fontStyle: "italic",
                  marginBlockStart: theme.spacing(theme.tokens.spacing.md)
                }}
              >
                {JOIN_LATER_MESSAGE}
              </Typography>
            )}
          </Box>
        </Stack>
        {actionable && needsPhotoId && (
          <Typography sx={{ fontStyle: "italic", marginBlockStart: theme.spacing(theme.tokens.spacing.md) }}>
            Please take a moment to upload your ID.
          </Typography>
        )}
        {actionable && (
          /*
            `.mob-acts` is a column at `align-items: stretch`, which is what makes the primary CTA full
            width — the `Button` is `inline-flex` and stretching is the cross-axis default, so it needs
            no `inlineSize` of its own. The theme sets `useFlexGap: true` on every `Stack`, so
            `spacing` emits `gap` rather than a margin keyed to the `direction` prop, which is why
            overriding `flexDirection` through `sx` stays correct in both directions.
          */
          <Stack
            sx={{
              flexDirection: perViewport("column", "row"),
              alignItems: perViewport("stretch", "center"),
              marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
              /*
                `gap` in `sx` rather than the `spacing` prop, because the two compositions disagree
                about where the separation LIVES. `.acts` is a 24px gap; `.mob-acts` is `gap: 0` and
                lets `.mob-cancel-link`'s own 16px block padding do the work, so the tap target grows
                instead of the space between two of them. Expressing that needs a real zero here and a
                padding below, which `spacing` alone cannot say.
              */
              gap: perViewport(theme.spacing(theme.tokens.spacing.none), theme.spacing(theme.tokens.spacing.lg))
            }}
          >
            <PrimaryAction
              action={action}
              joinUrl={resolvedJoinUrl}
              canJoin={canJoin}
              formsUrl={formsUrl}
              onPrepareForVisit={() => onPrepareForVisit?.(id)}
              onJoin={() => track({ type: "JOIN_CLICKED", appointmentId: id })}
            />
            {imminent && needsPhotoId && (
              <Button
                variant="outlined"
                color="secondary"
                component={ReactRouterLink}
                to={formsUrl}
                onClick={() => onPrepareForVisit?.(id)}
                sx={{
                  marginBlockStart: perViewport(
                    theme.spacing(theme.tokens.spacing.md),
                    theme.spacing(theme.tokens.spacing.none)
                  )
                }}
              >
                Upload ID
              </Button>
            )}
            {/* `.mob-cancel-link { align-self: center }` — centred under the CTA rather than stretched. */}
            <Link
              component="button"
              type="button"
              onClick={() => onRequestCancel(appointment)}
              sx={{
                alignSelf: perViewport("center", "auto"),
                paddingBlock: perViewport(
                  theme.spacing(theme.tokens.spacing.md),
                  theme.spacing(theme.tokens.spacing.none)
                )
              }}
            >
              Cancel
            </Link>
          </Stack>
        )}
      </Paper>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={copyState !== "idle"}
        autoHideDuration={COPY_CONFIRMATION_MS}
        onClose={() => setCopyState("idle")}
      >
        <Alert severity={copyState === "failed" ? "error" : "success"} onClose={() => setCopyState("idle")}>
          {copyState === "failed" ? COPY_FAILED_MESSAGE : COPY_SUCCESS_MESSAGE}
        </Alert>
      </Snackbar>
    </>
  )
}

export function UpNextAppointments({
  appointments,
  onRequestCancel,
  bookingPath,
  onPrepareForVisit
}: {
  appointments: UpcomingAppointment[]
  onRequestCancel: (appointment: UpcomingAppointment) => void
  bookingPath: string
  onPrepareForVisit?: (appointmentId: string) => void
}): JSX.Element | null {
  const theme = useTheme()

  if (appointments.length === 0) {
    return null
  }

  return (
    <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Typography variant="h3" component="h2">
        Upcoming
      </Typography>
      {appointments.map((appointment) => (
        <UpNextCard
          key={appointment.id}
          appointment={appointment}
          onPrepareForVisit={onPrepareForVisit}
          onRequestCancel={onRequestCancel}
          bookingPath={bookingPath}
        />
      ))}
    </Stack>
  )
}
